// The in-memory API's demo data for the gate tracker: the demo operator company's gate metrics
// (PLATFORM-OPERATOR: gates are KPI data — metric definitions carrying a gate spec — never code).
// Mirrors the dev seed's fixture (services/api/src/seed/fixtures/gate.ts).
import type { GateDef } from "@companyos/domain-kpi";
import { COMPANY_IDS } from "./fake-data";

/** The demo install's operator company (the marked company, not found by slug). */
export const FAKE_OPERATOR_COMPANY_ID = COMPANY_IDS.htva;

const FROM = "2026-10-16";

export const FAKE_GATE_DEFS: readonly GateDef[] = [
  {
    key: "gate.pitches",
    label: "Warm pitches",
    unit: "number",
    direction: "gte",
    spec: {
      order: 1,
      measure: "sum",
      condition: "Sales gate",
      count_from: FROM,
      due_on: "2026-11-15",
      target: 20,
    },
  },
  {
    key: "gate.pitch_declined",
    label: "Declined pitches",
    unit: "number",
    direction: "lte",
    spec: {
      order: 2,
      measure: "sum",
      condition: "Every “no” reason logged",
      count_from: FROM,
      notes_as_reasons: true,
    },
  },
  {
    key: "gate.close_rate",
    label: "Close rate",
    unit: "percent",
    direction: "between",
    spec: {
      order: 3,
      measure: "ratio",
      condition: "< 30 %: rework the offer; > 80 %: raise the price",
      count_from: FROM,
      target: 0.3,
      target_max: 0.8,
      ratio: { numerator: "gate.deposits", denominator: "gate.pitches" },
      notes: {
        none: "No pitches logged yet",
        below: "< 30 %: rework the offer",
        above: "> 80 %: raise the price",
      },
    },
  },
  {
    key: "gate.deposits",
    label: "Qualifying deposits (≥ $5k, client ≥ 6 months)",
    unit: "number",
    direction: "gte",
    spec: {
      order: 4,
      measure: "sum",
      condition: "Deposit gate",
      count_from: FROM,
      due_on: "2026-11-30",
      target: 2,
      kill: [
        {
          text: "fewer than 2 qualifying deposits by November 30 means rework the offer, not the platform.",
          due_on: "2026-11-30",
          below: 2,
        },
      ],
    },
  },
  {
    key: "gate.deposit_amount",
    label: "Deposit cash",
    unit: "currency",
    direction: "gte",
    spec: { order: 5, measure: "sum", condition: "Deposit gate", count_from: FROM },
  },
  {
    key: "gate.installs",
    label: "Paid installs",
    unit: "number",
    direction: "gte",
    spec: {
      order: 6,
      measure: "sum",
      condition: "Install gate",
      count_from: FROM,
      due_on: "2027-01-15",
      target: 3,
      kill: [
        {
          text: "Fewer than 2 paid installs by January 15, 2027 after a reworked offer means DEs stay a services line and no platform build starts.",
          due_on: "2027-01-15",
          below: 2,
        },
      ],
    },
  },
  {
    key: "gate.install_cash_multiple",
    label: "30-day cash ÷ (acquisition + delivery)",
    unit: "number",
    direction: "gte",
    spec: {
      order: 7,
      measure: "mean",
      condition: "Per-install condition",
      count_from: FROM,
      target: 2,
      notes: { none: "No install measured yet", below: "{n} install(s) below {target}×" },
    },
  },
  {
    key: "gate.refunds",
    label: "Refunds",
    unit: "number",
    direction: "lte",
    spec: {
      order: 8,
      measure: "sum",
      condition: "Day 60: zero refunds",
      count_from: FROM,
      target: 0,
    },
  },
  {
    key: "gate.supervisor_hours_per_de",
    label: "Supervisor h / DE / week",
    unit: "time",
    direction: "lte",
    spec: {
      order: 9,
      measure: "fanout",
      fanout: "supervision",
      condition: "Build trigger: above 10 h a week per DE",
      target: 600,
    },
  },
  {
    key: "gate.founder_hours_per_de",
    label: "Founder h / DE / week",
    unit: "time",
    direction: "lte",
    spec: {
      order: 10,
      measure: "fanout",
      fanout: "founder",
      condition: "Day 60: founder under 5 h a week",
      target: 300,
    },
  },
  {
    key: "gate.htva_recurring_revenue",
    label: "HTVA recurring revenue",
    unit: "currency",
    direction: "gte",
    spec: {
      order: 11,
      measure: "baseline",
      condition: "Protect HTVA: ≥ 90 % of the October baseline",
      baseline: {
        period_key: "2026-10",
        starts_on: "2026-10-01",
        label: "October baseline",
        review_at: 0.9,
        halt_at: 0.8,
        review_text: "October is the revenue baseline: down 10% triggers a review",
        halt_text: "October is the revenue baseline: down 20% halts the build.",
      },
      notes: { none: "No October baseline" },
    },
  },
  {
    key: "gate.cash_floor",
    label: "Cash on hand",
    unit: "currency",
    direction: "gte",
    spec: {
      order: 12,
      measure: "latest",
      condition: "Cash floor: below it, any build stops",
      target: 5_500_000,
      notes: { none: "Nothing logged yet", below: "Below the cash floor: any build stops" },
    },
  },
  {
    key: "gate.delivery_posts",
    label: "Public delivery posts",
    unit: "number",
    direction: "gte",
    spec: {
      order: 13,
      measure: "sum",
      condition: "Attention",
      count_from: FROM,
      due_on: "2026-11-30",
      target: 20,
    },
  },
  {
    key: "gate.delivery_failures",
    label: "Documented delivery failures on existing tools",
    unit: "number",
    direction: "gte",
    spec: { order: 14, measure: "sum", condition: "Build trigger", count_from: FROM },
  },
];
